import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";

const STATUS = {
  success: { label: "Erfolgreich", cls: "text-emerald-700", icon: CheckCircle2 },
  partial: { label: "Erfolgreich mit Hinweisen", cls: "text-amber-700", icon: AlertTriangle },
  failed: { label: "Fehlgeschlagen", cls: "text-red-700", icon: XCircle },
  not_configured: { label: "Keine Quelle", cls: "text-faint", icon: Clock },
  running: { label: "Läuft", cls: "text-dim", icon: RefreshCw },
};
const card = "rounded-xl border border-line bg-surface p-6";
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("de-AT", { dateStyle: "short", timeStyle: "short" }) : "");

const errText = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fallback;
};

const pollRun = async (id, onUpdate) => {
  for (;;) {
    await new Promise((r) => setTimeout(r, 2000));
    const { data } = await api.get(`/admin/sync/runs/${id}`);
    onUpdate(data);
    if (data.status !== "running") return data;
  }
};

const counts = (r) =>
  `+${r.added || 0} neu · ${r.updated || 0} aktualisiert · ${r.unchanged || 0} unverändert · ${r.deactivated || 0} deaktiviert`;

const Progress = ({ run }) => {
  const total = run.photos_total || 0;
  const pct = total ? Math.round(((run.photos_done || 0) / total) * 100) : 0;
  return (
    <div data-testid="sync-progress" className="rounded-lg border border-line bg-elevated/50 p-5">
      <p className="text-sm font-semibold text-ink flex items-center gap-2">
        <RefreshCw size={14} className="animate-spin text-brand" /> {run.phase || "Synchronisierung läuft …"}
      </p>
      {run.result_status && <p className="text-xs text-dim mt-2" data-testid="sync-progress-counts">Fahrzeuge online: {counts(run)}</p>}
      {total > 0 && (
        <div className="mt-4">
          <div className="h-2 rounded-full bg-line overflow-hidden">
            <div data-testid="sync-progress-bar" className="h-full bg-brand transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-dim mt-2" data-testid="sync-photos-count">{run.photos_done || 0} von {total} Fotos gespeichert</p>
        </div>
      )}
    </div>
  );
};

const RunSummary = ({ run, testId }) => {
  const s = STATUS[run.status] || STATUS.running;
  const Icon = s.icon;
  const msgs = [...(run.errors || []), ...(run.warnings || [])];
  return (
    <div data-testid={testId} className="rounded-lg border border-line bg-elevated/50 p-4">
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <span className={`inline-flex items-center gap-1.5 font-semibold ${s.cls}`}><Icon size={14} /> {s.label}</span>
        <span className="text-faint">{fmtTime(run.started_at)}</span>
      </div>
      {!["not_configured", "running"].includes(run.status) && <p className="text-xs text-dim mt-2">{counts(run)}</p>}
      {msgs.length > 0 && (
        <ul className="mt-2 space-y-1">
          {msgs.slice(0, 3).map((m, i) => <li key={i} className="text-xs text-amber-800 break-words">• {m}</li>)}
          {msgs.length > 3 && <li className="text-xs text-faint">… und {msgs.length - 3} weitere Hinweise</li>}
        </ul>
      )}
    </div>
  );
};

export const AdminSync = ({ onSynced }) => {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const following = useRef(false);

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([api.get("/admin/sync/status"), api.get("/admin/sync/runs?limit=5")]);
    setStatus(s.data);
    setRuns(r.data);
    return s.data;
  }, []);

  const follow = useCallback(async (id) => {
    if (following.current) return;
    following.current = true;
    setBusy(true);
    let shown = false;
    try {
      const done = await pollRun(id, (d) => {
        setRun(d);
        if (!shown && d.result_status) {
          shown = true;
          onSynced();
        }
      });
      if (done.status === "failed") toast.error("Synchronisierung fehlgeschlagen");
      else toast.success(`Synchronisierung abgeschlossen: +${done.added} neu, ${done.updated} aktualisiert`);
    } catch (err) {
      toast.error(errText(err, "Status konnte nicht geladen werden"));
    }
    following.current = false;
    setBusy(false);
    load();
    onSynced();
  }, [load, onSynced]);

  useEffect(() => {
    load()
      .then((s) => s.last_run?.status === "running" && follow(s.last_run.id))
      .catch(() => toast.error("Status konnte nicht geladen werden"));
  }, [load, follow]);

  const syncNow = async () => {
    setNotice("");
    setRun(null);
    try {
      const { data } = await api.post("/admin/sync/run");
      setRun(data);
      follow(data.id);
    } catch (err) {
      const msg = errText(err, "Synchronisierung fehlgeschlagen");
      setNotice(msg);
      toast.error(msg);
    }
  };

  if (!status) return <div className="py-16 flex justify-center"><div className="w-7 h-7 border-2 border-line border-t-brand rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-sync">
      <div className={`${card} md:p-8`} data-testid="sync-panel">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <p className="font-display font-bold text-xl text-ink">Fahrzeugabgleich</p>
            <p className="text-sm text-dim mt-1.5" data-testid="sync-summary">
              Quelle: gebrauchtwagen.at · {status.vehicles_public} Fahrzeuge online
              {status.last_success?.finished_at ? ` · zuletzt ${fmtTime(status.last_success.finished_at)}` : ""}
            </p>
            {status.gebrauchtwagen?.authorized && status.gebrauchtwagen?.auto_sync && (
              <p className="text-xs text-emerald-700 mt-2 inline-flex items-center gap-1.5" data-testid="sync-schedule">
                <Clock size={13} /> Automatischer Abgleich {status.schedule}
              </p>
            )}
          </div>
          <button data-testid="sync-now-btn" onClick={syncNow} disabled={busy}
            className="btn-pill inline-flex items-center justify-center gap-2 bg-action text-action-ink hover:bg-action-hover font-semibold px-7 py-3.5 text-sm disabled:opacity-60 shrink-0">
            <RefreshCw size={15} className={busy ? "animate-spin" : ""} /> {busy ? "Synchronisiere…" : "Jetzt synchronisieren"}
          </button>
        </div>
        {notice && <p data-testid="sync-notice" className="mt-5 text-sm text-amber-800 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3">{notice}</p>}
        {run && <div className="mt-5">{run.status === "running" ? <Progress run={run} /> : <RunSummary run={run} testId="sync-result" />}</div>}
      </div>

      <div data-testid="sync-runs">
        <p className="text-[10px] uppercase tracking-[0.2em] text-faint font-semibold mb-3">Letzte Abgleiche</p>
        <div className="space-y-3">
          {runs.length === 0 && <p className="text-sm text-faint">Noch keine Abgleiche.</p>}
          {runs.map((r) => <RunSummary key={r.id} run={r} testId={`sync-run-${r.id}`} />)}
        </div>
      </div>
    </div>
  );
};

export default AdminSync;
